import { create } from 'zustand';
import { GalleryImage } from '../types/gallery';
import { ApiStorageService } from '../services/ApiStorageService';
import { GalleryService } from '../services/GalleryService';
import { useNotificationStore } from './notificationStore';

interface AppState {
	// Data
	galleryImages: GalleryImage[];
	galleryTotal: number;
	galleryPage: number;
	galleryHasMore: boolean;
	galleryLoadingMore: boolean;

	// Loading states
	isLoading: boolean;
	loadingMessage: string;

	// Error state
	error: string | null;
}

interface AppActions {
	// Gallery management
	uploadGalleryImage: (file: File, opts?: { title?: string; tags?: string[] }) => Promise<GalleryImage>;
	refreshGallery: () => Promise<void>;
	loadMoreGallery: () => Promise<void>;
	deleteGalleryImage: (id: string) => Promise<void>;
	renameGalleryImage: (id: string, title: string) => Promise<void>;
	analyzeGalleryImage: (id: string) => Promise<void>;

	// Error handling
	setError: (error: string | null) => void;
	clearError: () => void;

	// Loading state
	setLoading: (loading: boolean, message?: string) => void;
}

type AppStore = AppState & AppActions;

const storageService = new ApiStorageService();
const galleryService = new GalleryService();

export const useAppStore = create<AppStore>((set, get) => ({
	// Initial state
	galleryImages: [],
	galleryTotal: 0,
	galleryPage: 1,
	galleryHasMore: false,
	galleryLoadingMore: false,
	isLoading: false,
	loadingMessage: '',
	error: null,

	// Gallery management
	uploadGalleryImage: async (file: File, opts?: { title?: string; tags?: string[] }) => {
		try {
			set({ isLoading: true, loadingMessage: 'Uploading image...' });
			const galleryImage = await storageService.uploadGalleryImage(file, opts);
			await get().refreshGallery();
			set({ isLoading: false, error: null });
			useNotificationStore.getState().success('Image uploaded');
			return galleryImage;
		} catch (error) {
			set({ 
				error: error instanceof Error ? error.message : 'Failed to upload image',
				isLoading: false
			});
			useNotificationStore.getState().error('Failed to upload image');
			throw error;
		}
	},

	refreshGallery: async () => {
		try {
			const result = await galleryService.listGalleryImagesPaginated({ page: 1, pageSize: 50 });
			set({
				galleryImages: result.items,
				galleryTotal: result.total,
				galleryPage: 1,
				galleryHasMore: result.hasMore,
			});
		} catch (error) {
			console.error('Failed to refresh gallery:', error);
		}
	},

	loadMoreGallery: async () => {
		const { galleryHasMore, galleryLoadingMore, galleryPage, galleryImages } = get();
		if (!galleryHasMore || galleryLoadingMore) return;
		try {
			set({ galleryLoadingMore: true });
			const nextPage = galleryPage + 1;
			const result = await galleryService.listGalleryImagesPaginated({ page: nextPage, pageSize: 50 });
			set({
				galleryImages: [...galleryImages, ...result.items],
				galleryPage: nextPage,
				galleryHasMore: result.hasMore,
				galleryLoadingMore: false,
			});
		} catch (error) {
			console.error('Failed to load more gallery images:', error);
			set({ galleryLoadingMore: false });
		}
	},

	deleteGalleryImage: async (id: string) => {
		try { 
			set({ isLoading: true, loadingMessage: 'Deleting image...' }); 
			await storageService.deleteGalleryImage(id); 
			await get().refreshGallery(); 
			set({ isLoading: false, error: null }); 
			useNotificationStore.getState().success('Image deleted');
		} catch (error) { 
			set({ error: error instanceof Error ? error.message : 'Failed to delete image', isLoading: false });
			useNotificationStore.getState().error('Failed to delete image');
		}
	},

	renameGalleryImage: async (id: string, title: string) => {
		try {
			set({ isLoading: true, loadingMessage: 'Renaming image...' });
			await storageService.renameGalleryImage(id, title);
			await get().refreshGallery();
			set({ isLoading: false, error: null });
			useNotificationStore.getState().success('Title updated');
		} catch (error) {
			set({ error: error instanceof Error ? error.message : 'Failed to rename image', isLoading: false });
			useNotificationStore.getState().error('Failed to rename image');
		}
	},

	analyzeGalleryImage: async (id: string) => {
		try {
			set({ isLoading: true, loadingMessage: 'Starting AI analysis...' });
			await storageService.analyzeGalleryImage(id);
			await get().refreshGallery();
			set({ isLoading: false, error: null });
			useNotificationStore.getState().success('AI analysis started');
		} catch (error) {
			set({ error: error instanceof Error ? error.message : 'Failed to start AI analysis', isLoading: false });
			useNotificationStore.getState().error('Failed to start AI analysis');
		}
	},

	// Error handling
	setError: (error: string | null) => {
		set({ error });
	},

	clearError: () => {
		set({ error: null });
	},

	// Loading state
	setLoading: (loading: boolean, message = '') => {
		set({ isLoading: loading, loadingMessage: message });
	},
}));
